import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSettings } from '../../context/SettingsContext';
import { useI18n } from '../../context/I18nContext';
import { useNotifications } from '../../context/NotificationContext';
import SettingsToggle from './SettingsToggle';
import notificationService from '../../services/notificationService';
import logger from '../../utils/logger';
import './NotificationsSettingsPage.css';

/**
 * Detect browser name from user agent for browser-specific instructions
 * @returns {'chrome'|'firefox'|'safari'|'edge'|'unknown'}
 */
const getBrowserName = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('chrome')) return 'chrome';
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('safari')) return 'safari';
  return 'unknown';
};

/**
 * Notifications Settings Page
 *
 * Provides UI for managing browser notification preferences:
 * - Permission status display and request
 * - Master toggle for browser notifications
 * - Granular toggles for different notification types
 * - Test notification functionality
 *
 * Permission Status Handling:
 * - 'unsupported': Browser doesn't support notifications
 * - 'denied': User blocked notifications (must change in browser settings)
 * - 'default': Permission not yet requested
 * - 'granted': Permission granted, show all settings
 */
const NotificationsSettingsPage = ({ onBack }) => {
  const { t } = useI18n();
  const { settings, updateSetting } = useSettings();
  const { permissionStatus, isSupported, requestPermission } = useNotifications();

  /**
   * Enable all notification sub-toggles when the master toggle is enabled.
   * Since the UI removed individual toggles, we ensure all types are enabled
   * to prevent legacy account data with disabled sub-toggles from blocking notifications.
   */
  const enableAllNotificationTypes = useCallback(() => {
    updateSetting('notifyNewMessages', true);
    updateSetting('notifyInvites', true);
    updateSetting('notifyReactions', true);
    updateSetting('notifyMentions', true);
  }, [updateSetting]);

  const handleRequestPermission = useCallback(async () => {
    logger.debug('[NotificationsSettingsPage] Enable button clicked, requesting permission...');
    const result = await requestPermission();
    logger.debug('[NotificationsSettingsPage] Permission result:', result);
    // Auto-enable the browserNotifications setting when permission is granted
    // This ensures notifications work immediately after user grants permission
    if (result === 'granted') {
      updateSetting('browserNotifications', true);
      enableAllNotificationTypes();
    }
  }, [requestPermission, updateSetting, enableAllNotificationTypes]);

  const handleTestNotification = useCallback(() => {
    // Bypass the context's visibility check by calling the service directly
    notificationService.showNotification({
      eventId: `test-${Date.now()}`,
      roomId: 'test-room',
      title: t('Notifications.testNotificationTitle'),
      body: t('Notifications.testNotificationBody'),
      icon: '/images/AppIcon60x60.png',
      playSound: settings.notifySound,
    });
  }, [t, settings.notifySound]);

  /**
   * Render permission status section based on current state
   */
  const renderPermissionStatus = () => {
    if (!isSupported) {
      return (
        <div className="notification-status-box warning">
          <ion-icon name="alert-circle-outline" />
          <div className="status-text">
            <div className="status-title">{t('Notifications.browserNotSupported')}</div>
            <div className="status-description">
              {t('Notifications.browserNotSupportedDesc')}
            </div>
          </div>
        </div>
      );
    }

    if (permissionStatus === 'denied') {
      const browserName = getBrowserName();

      return (
        <div className="notification-status-box warning">
          <ion-icon name="notifications-off-outline" />
          <div className="status-text">
            <div className="status-title">{t('Notifications.notificationsBlocked')}</div>
            <div className="status-description">
              {t('Notifications.notificationsBlockedDesc')}
            </div>
            <ol className="browser-instructions">
              {browserName === 'chrome' && (
                <>
                  <li>{t('Notifications.chromeStep1')}</li>
                  <li>{t('Notifications.chromeStep2')}</li>
                  <li>{t('Notifications.chromeStep3')}</li>
                </>
              )}
              {browserName === 'firefox' && (
                <>
                  <li>{t('Notifications.firefoxStep1')}</li>
                  <li>{t('Notifications.firefoxStep2')}</li>
                  <li>{t('Notifications.firefoxStep3')}</li>
                </>
              )}
              {browserName === 'safari' && (
                <>
                  <li>{t('Notifications.safariStep1')}</li>
                  <li>{t('Notifications.safariStep2')}</li>
                </>
              )}
              {browserName === 'edge' && (
                <>
                  <li>{t('Notifications.edgeStep1')}</li>
                  <li>{t('Notifications.edgeStep2')}</li>
                  <li>{t('Notifications.edgeStep3')}</li>
                </>
              )}
              {browserName === 'unknown' && (
                <>
                  <li>{t('Notifications.unknownBrowserStep1')}</li>
                  <li>{t('Notifications.unknownBrowserStep2')}</li>
                  <li>{t('Notifications.unknownBrowserStep3')}</li>
                  <li>{t('Notifications.unknownBrowserStep4')}</li>
                </>
              )}
            </ol>
          </div>
        </div>
      );
    }

    if (permissionStatus === 'default') {
      return (
        <div className="notification-status-box info">
          <ion-icon name="notifications-outline" />
          <div className="status-text">
            <div className="status-title">{t('Notifications.enableNotificationsTitle')}</div>
            <div className="status-description">
              {t('Notifications.enableNotificationsDesc')}
            </div>
          </div>
          <button
            className="enable-notifications-button"
            onClick={handleRequestPermission}
          >
            {t('Notifications.enableBrowserNotifications')}
          </button>
        </div>
      );
    }

    // Permission granted
    return (
      <div className="notification-status-box success">
        <ion-icon name="checkmark-circle-outline" />
        <div className="status-text">
          <div className="status-title">{t('Notifications.notificationsEnabled')}</div>
          <div className="status-description">
            {t('Notifications.notificationsEnabledDesc')}
          </div>
        </div>
      </div>
    );
  };

  const isPermissionGranted = permissionStatus === 'granted';
  const masterToggleEnabled = settings.browserNotifications;

  return (
    <div className="notifications-settings-page">
      <div className="settings-modal-header">
        <button
          className="back-button"
          onClick={onBack}
          aria-label={t('Notifications.goBack')}
        >
          <ion-icon name="chevron-back-outline" />
        </button>
        <span className="settings-page-title">{t('Notifications.pageTitle')}</span>
      </div>

      <div className="notifications-settings-content">
        {/* Permission Status Section */}
        <div className="notifications-section">
          <div className="notifications-section-title">{t('Notifications.permissionStatusTitle')}</div>
          {renderPermissionStatus()}
        </div>

        {/* Notification Settings Section - only show when permission granted */}
        {isPermissionGranted && (
          <>
            <div className="notifications-section">
              <div className="notifications-section-title">{t('Notifications.settingsTitle')}</div>
              <div className="notifications-toggles">
                <SettingsToggle
                  icon="fa-bell"
                  label={t('Notifications.browserNotificationsToggle')}
                  description={t('Notifications.browserNotificationsDesc')}
                  checked={settings.browserNotifications}
                  onChange={(value) => {
                    updateSetting('browserNotifications', value);
                    // When enabling the master toggle, also enable all sub-toggles
                    // since the UI no longer has individual controls for them
                    if (value) {
                      enableAllNotificationTypes();
                    }
                  }}
                />
                <SettingsToggle
                  icon="fa-volume-high"
                  label={t('Notifications.soundToggle')}
                  description={t('Notifications.soundDesc')}
                  checked={settings.notifySound}
                  onChange={(value) => updateSetting('notifySound', value)}
                  disabled={!masterToggleEnabled}
                />
              </div>
            </div>

            {/* Test Notification Section */}
            <div className="notifications-section">
              <div className="notifications-section-title">{t('Notifications.testTitle')}</div>
              <div className="test-notification-container">
                <p className="test-description">
                  {t('Notifications.testDescription')}
                </p>
                <button
                  className="test-notification-button"
                  onClick={handleTestNotification}
                  disabled={!masterToggleEnabled}
                >
                  {t('Notifications.sendTestNotification')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

NotificationsSettingsPage.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default NotificationsSettingsPage;
